import React, { useState } from 'react';
import {
  Users,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart as PieChartIcon,
  Calendar,
  MapPin,
  Heart,
  Activity,
  Target,
  Download,
  Filter,
  RefreshCw,
  Eye,
  Building,
  UserCheck,
  Clock,
  DollarSign,
  Package,
  AlertTriangle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import StatCard from '../../components/StatCard';

interface DemographicData {
  ageGroup: string;
  count: number;
  percentage: number;
  revenue: number;
  averageSpend: number;
  topMedications: string[];
  chronicConditions: string[];
  complianceRate: number;
}

interface GeographicData {
  suburb: string;
  postcode: string;
  patientCount: number;
  revenue: number;
  averageAge: number;
  topConditions: string[];
}

interface CustomerSegmentData {
  segment: string;
  patientCount: number;
  revenue: number;
  growthRate: number;
  retentionRate: number;
  averageOrderValue: number;
  medicationCompliance: number;
  satisfactionScore: number;
}

const DemographicReports: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('12_months');
  const [selectedView, setSelectedView] = useState<'overview' | 'age' | 'geographic' | 'segments' | 'conditions'>('overview');
  const [selectedMetric, setSelectedMetric] = useState('patient_count');

  // Mock demographic data
  const ageGroupData: DemographicData[] = [
    {
      ageGroup: '0-17',
      count: 1250,
      percentage: 8.5,
      revenue: 125000,
      averageSpend: 100,
      topMedications: ['Paracetamol', 'Ventolin', 'Amoxicillin'],
      chronicConditions: ['Asthma', 'ADHD', 'Allergies'],
      complianceRate: 85.2
    },
    {
      ageGroup: '18-34',
      count: 2100,
      percentage: 14.3,
      revenue: 315000,
      averageSpend: 150,
      topMedications: ['Contraceptives', 'Sertraline', 'Ventolin'],
      chronicConditions: ['Depression', 'Anxiety', 'Asthma'],
      complianceRate: 78.9
    },
    {
      ageGroup: '35-49',
      count: 2800,
      percentage: 19.1,
      revenue: 560000,
      averageSpend: 200,
      topMedications: ['Atorvastatin', 'Metformin', 'Pantoprazole'],
      chronicConditions: ['Hypertension', 'Diabetes', 'High Cholesterol'],
      complianceRate: 82.4
    },
    {
      ageGroup: '50-64',
      count: 3500,
      percentage: 23.8,
      revenue: 875000,
      averageSpend: 250,
      topMedications: ['Atorvastatin', 'Metoprolol', 'Pantoprazole'],
      chronicConditions: ['Hypertension', 'Diabetes', 'Arthritis'],
      complianceRate: 88.7
    },
    {
      ageGroup: '65-79',
      count: 3800,
      percentage: 25.9,
      revenue: 1140000,
      averageSpend: 300,
      topMedications: ['Warfarin', 'Furosemide', 'Digoxin'],
      chronicConditions: ['Heart Disease', 'Diabetes', 'Osteoporosis'],
      complianceRate: 92.1
    },
    {
      ageGroup: '80+',
      count: 1200,
      percentage: 8.2,
      revenue: 480000,
      averageSpend: 400,
      topMedications: ['OxyContin', 'Furosemide', 'Warfarin'],
      chronicConditions: ['Dementia', 'Heart Failure', 'Osteoporosis'],
      complianceRate: 94.5
    }
  ];

  const geographicData: GeographicData[] = [
    {
      suburb: 'Sydney CBD',
      postcode: '2000',
      patientCount: 2500,
      revenue: 625000,
      averageAge: 42,
      topConditions: ['Hypertension', 'Anxiety', 'High Cholesterol']
    },
    {
      suburb: 'Bondi',
      postcode: '2026',
      patientCount: 1800,
      revenue: 450000,
      averageAge: 38,
      topConditions: ['Anxiety', 'Depression', 'Asthma']
    },
    {
      suburb: 'Parramatta',
      postcode: '2150',
      patientCount: 3200,
      revenue: 800000,
      averageAge: 45,
      topConditions: ['Diabetes', 'Hypertension', 'High Cholesterol']
    },
    {
      suburb: 'Manly',
      postcode: '2095',
      patientCount: 1500,
      revenue: 375000,
      averageAge: 48,
      topConditions: ['Arthritis', 'Hypertension', 'Osteoporosis']
    },
    {
      suburb: 'Blacktown',
      postcode: '2148',
      patientCount: 2800,
      revenue: 560000,
      averageAge: 41,
      topConditions: ['Diabetes', 'Asthma', 'Depression']
    },
    {
      suburb: 'Chatswood',
      postcode: '2067',
      patientCount: 2200,
      revenue: 660000,
      averageAge: 52,
      topConditions: ['High Cholesterol', 'Hypertension', 'Diabetes']
    }
  ];

  const customerSegmentData: CustomerSegmentData[] = [
    {
      segment: 'Aged Care Residents',
      patientCount: 4200,
      revenue: 1680000,
      growthRate: 8.5,
      retentionRate: 95.2,
      averageOrderValue: 400,
      medicationCompliance: 94.8,
      satisfactionScore: 4.6
    },
    {
      segment: 'NDIS Participants',
      patientCount: 1800,
      revenue: 540000,
      growthRate: 15.2,
      retentionRate: 88.9,
      averageOrderValue: 300,
      medicationCompliance: 86.4,
      satisfactionScore: 4.4
    },
    {
      segment: 'Chronic Disease Patients',
      patientCount: 5500,
      revenue: 1375000,
      growthRate: 6.8,
      retentionRate: 92.1,
      averageOrderValue: 250,
      medicationCompliance: 89.7,
      satisfactionScore: 4.5
    },
    {
      segment: 'Retail Customers',
      patientCount: 8200,
      revenue: 820000,
      growthRate: 3.2,
      retentionRate: 65.8,
      averageOrderValue: 100,
      medicationCompliance: 72.3,
      satisfactionScore: 4.2
    },
    {
      segment: 'Mental Health Patients',
      patientCount: 2100,
      revenue: 630000,
      growthRate: 12.4,
      retentionRate: 84.6,
      averageOrderValue: 300,
      medicationCompliance: 78.9,
      satisfactionScore: 4.3
    }
  ];

  const conditionData = [
    { condition: 'Hypertension', patients: 4500, prevalence: 30.6, averageAge: 58, annualCost: 1125000 },
    { condition: 'Diabetes Type 2', patients: 3200, prevalence: 21.8, averageAge: 55, annualCost: 960000 },
    { condition: 'High Cholesterol', patients: 3800, prevalence: 25.9, averageAge: 52, annualCost: 950000 },
    { condition: 'Depression', patients: 2100, prevalence: 14.3, averageAge: 42, annualCost: 630000 },
    { condition: 'Anxiety', patients: 1900, prevalence: 12.9, averageAge: 38, annualCost: 570000 },
    { condition: 'Asthma', patients: 1600, prevalence: 10.9, averageAge: 35, annualCost: 480000 },
    { condition: 'Arthritis', patients: 2200, prevalence: 15.0, averageAge: 62, annualCost: 660000 },
    { condition: 'Heart Disease', patients: 1800, prevalence: 12.3, averageAge: 65, annualCost: 720000 },
    { condition: 'Osteoporosis', patients: 1200, prevalence: 8.2, averageAge: 68, annualCost: 480000 },
    { condition: 'COPD', patients: 800, prevalence: 5.4, averageAge: 64, annualCost: 400000 }
  ];

  const monthlyTrends = [
    { month: 'Jan', newPatients: 145, revenue: 425000, avgAge: 48.2, complianceRate: 87.5 },
    { month: 'Feb', newPatients: 132, revenue: 398000, avgAge: 47.8, complianceRate: 88.1 },
    { month: 'Mar', newPatients: 158, revenue: 445000, avgAge: 49.1, complianceRate: 87.9 },
    { month: 'Apr', newPatients: 167, revenue: 478000, avgAge: 48.7, complianceRate: 88.8 },
    { month: 'May', newPatients: 142, revenue: 421000, avgAge: 47.9, complianceRate: 89.2 },
    { month: 'Jun', newPatients: 175, revenue: 495000, avgAge: 49.3, complianceRate: 88.6 },
    { month: 'Jul', newPatients: 189, revenue: 523000, avgAge: 48.9, complianceRate: 89.4 },
    { month: 'Aug', newPatients: 156, revenue: 467000, avgAge: 48.4, complianceRate: 89.1 },
    { month: 'Sep', newPatients: 171, revenue: 489000, avgAge: 49.0, complianceRate: 89.7 }
  ];

  const colors = ['#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#84CC16', '#F97316', '#EC4899'];


  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Demographic Reports
          </h1>
          <p className="text-gray-600 mt-2">Comprehensive patient demographics and population health analytics</p>
        </div>
        <div className="flex space-x-3">
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
          <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="3_months">Last 3 Months</option>
            <option value="6_months">Last 6 Months</option>
            <option value="12_months">Last 12 Months</option>
            <option value="24_months">Last 24 Months</option>
          </select>
          
          <select
            value={selectedView}
            onChange={(e) => setSelectedView(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="overview">Overview</option>
            <option value="age">Age Demographics</option>
            <option value="geographic">Geographic Analysis</option>
            <option value="segments">Customer Segments</option>
            <option value="conditions">Health Conditions</option>
          </select>

          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="patient_count">Patient Count</option>
            <option value="revenue">Revenue</option>
            <option value="compliance">Compliance Rate</option>
            <option value="satisfaction">Satisfaction Score</option>
          </select>

          <button className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Calendar className="h-4 w-4" />
            <span>Date Range</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value="14,650"
          subtitle="Active patients"
          icon={<Users className="h-6 w-6" />}
          trend="vs last month"
          trendValue={8.2}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Average Age"
          value="48.7 years"
          subtitle="Median: 52 years"
          icon={<UserCheck className="h-6 w-6" />}
          trend="vs last month"
          trendValue={1.2}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Compliance Rate"
          value="88.9%"
          subtitle="Medication adherence"
          icon={<Target className="h-6 w-6" />}
          trend="vs last month"
          trendValue={2.4}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Revenue per Patient"
          value="$285"
          subtitle="Annual average"
          icon={<DollarSign className="h-6 w-6" />}
          trend="vs last month"
          trendValue={5.8}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Main Content based on selected view */}
      {selectedView === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Age Distribution */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Age Distribution</h3>
                <p className="text-gray-600 text-sm">Patient demographics by age group</p>
              </div>
              <BarChart3 className="h-5 w-5 text-gray-600" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ageGroupData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="ageGroup" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: 'none', 
                    borderRadius: '12px', 
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                  }} 
                />
                <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Trends */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Monthly Trends</h3>
                <p className="text-gray-600 text-sm">New patients and revenue trends</p>
              </div>
              <Activity className="h-5 w-5 text-gray-600" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: 'none', 
                    borderRadius: '12px', 
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                  }} 
                />
                <Line 
                  type="monotone" 
                  dataKey="newPatients" 
                  stroke="#8B5CF6" 
                  strokeWidth={3}
                  dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {selectedView === 'age' && (
        <div className="space-y-8">
          {/* Age Group Analysis */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Detailed Age Group Analysis</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Age Group</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Patients</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Revenue</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Avg Spend</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Compliance</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Top Conditions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {ageGroupData.map((group, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-4 px-4 font-medium text-gray-900">{group.ageGroup}</td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{group.count.toLocaleString()}</p>
                          <p className="text-sm text-gray-600">{group.percentage}%</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 font-medium text-gray-900">
                        ${group.revenue.toLocaleString()}
                      </td>
                      <td className="py-4 px-4 font-medium text-gray-900">
                        ${group.averageSpend}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{ width: `${group.complianceRate}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{group.complianceRate}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-wrap gap-1">
                          {group.chronicConditions.slice(0, 2).map((condition, idx) => (
                            <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                              {condition}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedView === 'geographic' && (
        <div className="space-y-8">
          {/* Geographic Distribution */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Geographic Distribution</h3>
                <p className="text-gray-600 text-sm">Patient distribution by suburb</p>
              </div>
              <MapPin className="h-5 w-5 text-gray-600" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {geographicData.map((area, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{area.suburb}</h4>
                      <p className="text-sm text-gray-600">{area.postcode}</p>
                    </div>
                    <Building className="h-5 w-5 text-gray-500" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Patients:</span>
                      <span className="text-sm font-medium text-gray-900">{area.patientCount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Revenue:</span>
                      <span className="text-sm font-medium text-gray-900">${area.revenue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Avg Age:</span>
                      <span className="text-sm font-medium text-gray-900">{area.averageAge} years</span>
                    </div>
                    <div className="mt-3">
                      <p className="text-xs text-gray-600 mb-1">Top Conditions:</p>
                      <div className="flex flex-wrap gap-1">
                        {area.topConditions.slice(0, 2).map((condition, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {condition}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'segments' && (
        <div className="space-y-8">
          {/* Customer Segments */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Customer Segment Analysis</h3>
            <div className="space-y-6">
              {customerSegmentData.map((segment, index) => (
                <div key={index} className="p-6 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">{segment.segment}</h4>
                    <div className="flex items-center space-x-2">
                      {segment.growthRate >= 0 ? (
                        <TrendingUp className="h-4 w-4 text-green-500" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      )}
                      <span className={`text-sm font-medium ${segment.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {segment.growthRate >= 0 ? '+' : ''}{segment.growthRate}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Patients</p>
                      <p className="text-xl font-bold text-gray-900">{segment.patientCount.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Revenue</p>
                      <p className="text-xl font-bold text-gray-900">${segment.revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Avg Order Value</p>
                      <p className="text-xl font-bold text-gray-900">${segment.averageOrderValue}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Retention Rate</p>
                      <p className="text-xl font-bold text-gray-900">{segment.retentionRate}%</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Medication Compliance</span>
                        <span className="text-sm font-medium text-gray-900">{segment.medicationCompliance}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-500 h-2 rounded-full" 
                          style={{ width: `${segment.medicationCompliance}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Satisfaction Score</span>
                        <span className="text-sm font-medium text-gray-900">{segment.satisfactionScore}/5.0</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${(segment.satisfactionScore / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedView === 'conditions' && (
        <div className="space-y-8">
          {/* Health Conditions Analysis */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Health Conditions Analysis</h3>
                <p className="text-gray-600 text-sm">Most common conditions and their impact</p>
              </div>
              <Heart className="h-5 w-5 text-gray-600" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Condition</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Patients</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Prevalence</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Avg Age</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Annual Cost</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Trend</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {conditionData.map((condition, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="py-4 px-4 font-medium text-gray-900">{condition.condition}</td>
                      <td className="py-4 px-4 font-medium text-gray-900">{condition.patients.toLocaleString()}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full" 
                              style={{ width: `${(condition.prevalence / 35) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{condition.prevalence}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-gray-900">{condition.averageAge} years</td>
                      <td className="py-4 px-4 font-medium text-gray-900">${condition.annualCost.toLocaleString()}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-600">+{(Math.random() * 10).toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemographicReports;
